import { AiAgentCatalogItemAvailabilityEnum, AiInferenceOperation, AiProfile, AiTargetType } from '../../api';
import { BaseButton } from '../base/button/button';
import { BaseCheckbox } from '../base/checkbox/checkbox';
import { BaseRadio } from '../base/radio/radio';
import { Toggle } from '../base/toggle/toggle';
import { mdiClose, mdiDelete } from '@mdi/js';
import Icon from '@mdi/react';
import isEqual from 'lodash/isEqual';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { humanSeconds, OPERATIONS } from '../../services/ai/ai.derive';
import { validateProfile } from '../../services/ai/ai.form';
import { AiStore } from '../../stores/ai/ai.store';
import { AreaField, NumberField, StatusTag, TextField } from './ai-fields';
import * as S from './ai.styles';

const NEW_PROFILE: AiProfile = {
    id: '',
    displayName: '',
    description: '',
    enabled: true,
    targetType: AiTargetType.MODEL,
    models: [],
    defaultModel: '',
    operations: [AiInferenceOperation.CHATCOMPLETIONS],
    defaultLifetimeSeconds: 900,
    maxLifetimeSeconds: 1800,
    keyLimits: { maxBudgetUsd: 1 },
};

interface IProfileDrawerProps {
    store: AiStore;
    /** Index into the draft's profiles, or -1 for a new profile. */
    index: number;
    onClose: () => void;
    /** Called with whether the form holds edits that have not been applied to the draft. False when it closes. */
    onChangedChange?: (changed: boolean) => void;
}

export const ProfileDrawer = observer(({ store, index, onClose, onChangedChange }: IProfileDrawerProps): React.ReactElement => {
    // The profile is copied once when the drawer opens; later draft changes must not rewrite the form.
    const [original] = useState<AiProfile>(() => {
        const source = index === -1 ? NEW_PROFILE : store.draft!.profiles[index];
        const copy: AiProfile = JSON.parse(JSON.stringify(source));
        copy.models = copy.models ?? [];
        copy.keyLimits = copy.keyLimits ?? { maxBudgetUsd: 1 };
        return copy;
    });
    const [p, setP] = useState<AiProfile>(original);
    const [confirmClose, setConfirmClose] = useState(false);

    const update = (fn: (next: AiProfile) => void): void =>
        setP(prev => {
            const next: AiProfile = JSON.parse(JSON.stringify(prev));
            fn(next);
            return next;
        });

    const isModel = p.targetType === AiTargetType.MODEL;
    const otherIds = store.draft!.profiles.filter((_, i) => i !== index).map(x => x.id);
    const clientErrors = validateProfile(p, { otherIds, agents: store.agents });
    const serverErrors = index === -1 ? {} : store.serverProfileErrors[index] ?? {};
    const err = (key: string): string | undefined => clientErrors[key] ?? serverErrors[key];
    const valid = Object.keys(clientErrors).length === 0;
    const changed = !isEqual(p, original);

    // The form lives in this component, so the page cannot see edits that have not been applied yet. Tell it, so that
    // leaving the page or closing the tab asks first, as it already does for the applied draft.
    useEffect(() => {
        onChangedChange?.(changed);
    }, [changed]);
    useEffect(() => () => onChangedChange?.(false), []);

    const requestClose = (): void => (changed ? setConfirmClose(true) : onClose());

    const apply = (): void => {
        if (!valid) return;
        store.editDraft(d => {
            if (index === -1) d.profiles.push(p);
            else d.profiles[index] = p;
        });
        onClose();
    };

    const remove = (): void => {
        store.editDraft(d => {
            d.profiles.splice(index, 1);
        });
        onClose();
    };

    const modelNames = Array.from(new Set([...(store.models ?? []), ...(p.models ?? [])]));
    const toggleModel = (name: string): void =>
        update(n => {
            const has = (n.models ?? []).includes(name);
            n.models = has ? (n.models ?? []).filter(m => m !== name) : [...(n.models ?? []), name];
            if (has && n.defaultModel === name) n.defaultModel = n.models[0] ?? '';
            if (!has && !n.defaultModel) n.defaultModel = name;
        });

    return (
        <S.Drawer>
            <S.DrawerForm>
                <S.DrawerHead>
                    <div>
                        <S.DrawerTitle>{index === -1 ? 'New profile' : `Edit profile · ${p.id}`}</S.DrawerTitle>
                        <S.Hint>Applied to the draft. Save configuration to make it effective.</S.Hint>
                    </div>
                    <BaseButton variant="text" aria-label="Close" onClick={requestClose}>
                        <Icon path={mdiClose} size={0.85} />
                    </BaseButton>
                </S.DrawerHead>

                <S.DrawerBody>
                    <S.FormGrid>
                        <TextField
                            label="Profile ID"
                            value={p.id}
                            disabled={index !== -1}
                            placeholder="basic"
                            error={err('id')}
                            onChange={(v): void => update(n => void (n.id = v))}
                        />
                        <TextField
                            label="Display name"
                            value={p.displayName}
                            placeholder="Basic"
                            error={err('displayName')}
                            onChange={(v): void => update(n => void (n.displayName = v))}
                        />
                    </S.FormGrid>
                    <AreaField
                        label="Description · user-facing; no prompts, patient data, or credentials"
                        value={p.description}
                        onChange={(v): void => update(n => void (n.description = v))}
                    />

                    <S.ToggleCard>
                        <S.Grow>
                            <S.SectionTitle id="ai-profile-issuance-title">Issuance enabled</S.SectionTitle>
                            <S.Hint>Disabling does not revoke existing keys.</S.Hint>
                        </S.Grow>
                        {/* Named by the visible heading, so the spoken name cannot drift from the text on screen. */}
                        <Toggle
                            aria-labelledby="ai-profile-issuance-title"
                            value={p.enabled}
                            onChange={(v): void => update(n => void (n.enabled = v))}
                        />
                    </S.ToggleCard>

                    <S.Section>
                        <S.Label as="span" id="ai-profile-target-type">
                            Target type
                        </S.Label>
                        {/* Native radios from the design system: one choice of two, keyboard-operable, named by the label. */}
                        <div role="radiogroup" aria-labelledby="ai-profile-target-type">
                            {(
                                [
                                    [AiTargetType.MODEL, 'Model', 'Direct access to listed gateway models'],
                                    [AiTargetType.AGENT, 'Agent', 'Invoke one registered agent via a2a/'],
                                ] as const
                            ).map(([value, label, desc]) => (
                                <S.RadioOption key={value}>
                                    <BaseRadio
                                        name="ai-profile-target-type"
                                        label={label}
                                        checked={p.targetType === value}
                                        onChange={(): void =>
                                            update(n => {
                                                n.targetType = value;
                                                if (value === AiTargetType.AGENT) n.operations = [AiInferenceOperation.CHATCOMPLETIONS];
                                            })
                                        }
                                    />
                                    <S.Hint>{desc}</S.Hint>
                                </S.RadioOption>
                            ))}
                        </div>
                    </S.Section>

                    {isModel ? (
                        <S.Section>
                            <S.InlineRow>
                                <S.Label as="span">Models · from GET /support/ai/models</S.Label>
                                <S.Grow />
                                <S.Label as="span">Default</S.Label>
                            </S.InlineRow>
                            {store.models === undefined ? (
                                // NS-3: not loaded is not "no models".
                                <S.Empty>
                                    {store.catalogErrors.models
                                        ? `The model list could not be loaded: ${store.catalogErrors.models}`
                                        : 'The model list is not loaded yet.'}
                                </S.Empty>
                            ) : null}
                            <S.PickList>
                                {modelNames.map(name => {
                                    const on = (p.models ?? []).includes(name);
                                    return (
                                        <S.PickRow key={name} on={on}>
                                            <S.Grow>
                                                <BaseCheckbox
                                                    checked={on}
                                                    onChange={(): void => toggleModel(name)}
                                                    label={name}
                                                />
                                            </S.Grow>
                                            <BaseRadio
                                                // One shared name makes these a native group (arrow keys move the choice).
                                                name="ai-profile-default-model"
                                                aria-label={`Default model ${name}`}
                                                checked={p.defaultModel === name}
                                                disabled={!on}
                                                onChange={(): void => update(n => void (n.defaultModel = name))}
                                            />
                                        </S.PickRow>
                                    );
                                })}
                            </S.PickList>
                            {err('models') && <S.ErrorText>{err('models')}</S.ErrorText>}
                            {err('defaultModel') && <S.ErrorText>{err('defaultModel')}</S.ErrorText>}
                        </S.Section>
                    ) : (
                        <S.Section>
                            <S.Label as="span">Agent · from GET /support/ai/agents</S.Label>
                            {store.agents === undefined ? (
                                <S.Empty>
                                    {store.catalogErrors.agents
                                        ? `The agent list could not be loaded: ${store.catalogErrors.agents}`
                                        : 'The agent list is not loaded yet.'}
                                </S.Empty>
                            ) : (
                                <S.PickList>
                                    {store.agents.map(a => {
                                        const ready = a.availability === AiAgentCatalogItemAvailabilityEnum.READY;
                                        return (
                                            <S.PickRow key={a.id} on={p.agentId === a.id}>
                                                <BaseRadio
                                                    name="ai-profile-agent"
                                                    aria-label={`Agent ${a.name}`}
                                                    checked={p.agentId === a.id}
                                                    onChange={(): void => update(n => void (n.agentId = a.id))}
                                                />
                                                <S.Cell>
                                                    <S.InlineRow>
                                                        <S.Strong>{a.name}</S.Strong>
                                                        <StatusTag tone={ready ? 'success' : 'danger'}>{a.availability}</StatusTag>
                                                    </S.InlineRow>
                                                    <S.Mono>
                                                        {a.invocationModel} · {a.streamingSupported ? 'streaming' : 'no streaming'}
                                                    </S.Mono>
                                                    {a.unavailableReason && <S.Muted>{a.unavailableReason}</S.Muted>}
                                                </S.Cell>
                                            </S.PickRow>
                                        );
                                    })}
                                </S.PickList>
                            )}
                            {err('agentId') && <S.ErrorText>{err('agentId')}</S.ErrorText>}
                            <S.Hint>
                                Issued keys get the a2a/ name and the agent&apos;s canonical ID only — no direct model, other
                                agent, native A2A, or MCP routes.
                            </S.Hint>
                        </S.Section>
                    )}

                    <S.Section>
                        <S.Label as="span">Operations</S.Label>
                        <S.InlineRow>
                            {OPERATIONS.map(op => {
                                const on = isModel
                                    ? p.operations.includes(op.value)
                                    : op.value === AiInferenceOperation.CHATCOMPLETIONS;
                                return (
                                    <BaseCheckbox
                                        key={op.value}
                                        label={op.label}
                                        title={op.route}
                                        checked={on}
                                        // An agent profile is chat-only: shown ticked, and not changeable.
                                        disabled={!isModel}
                                        onChange={(): void =>
                                            update(n => {
                                                n.operations = n.operations.includes(op.value)
                                                    ? n.operations.filter(x => x !== op.value)
                                                    : [...n.operations, op.value];
                                            })
                                        }
                                    />
                                );
                            })}
                        </S.InlineRow>
                        {err('operations') && <S.ErrorText>{err('operations')}</S.ErrorText>}
                        <S.Hint>
                            {isModel
                                ? 'Each operation maps to its exact route plus /v1/models. Each operation needs a compatible listed model; the server validates.'
                                : 'Agent profiles allow only /v1/chat/completions.'}
                        </S.Hint>
                    </S.Section>

                    <S.Section>
                        <S.SectionTitle>Key lifetime</S.SectionTitle>
                        <S.FormGrid>
                            <NumberField
                                label="Default (seconds)"
                                min={60}
                                max={3600}
                                value={p.defaultLifetimeSeconds}
                                hint={humanSeconds(p.defaultLifetimeSeconds)}
                                onChange={(v): void => update(n => void (n.defaultLifetimeSeconds = v as number))}
                            />
                            <NumberField
                                label="Maximum (seconds)"
                                min={60}
                                max={3600}
                                value={p.maxLifetimeSeconds}
                                hint={humanSeconds(p.maxLifetimeSeconds)}
                                onChange={(v): void => update(n => void (n.maxLifetimeSeconds = v as number))}
                            />
                        </S.FormGrid>
                        {err('lifetime') && <S.ErrorText>{err('lifetime')}</S.ErrorText>}
                    </S.Section>

                    <S.Section>
                        <S.SectionTitle>Per-key limits</S.SectionTitle>
                        <S.FormGrid cols={3}>
                            <NumberField
                                label="Budget (USD)"
                                min={0}
                                step={0.5}
                                value={p.keyLimits.maxBudgetUsd}
                                onChange={(v): void => update(n => void (n.keyLimits.maxBudgetUsd = v as number))}
                            />
                            <NumberField
                                label="Requests / min"
                                min={1}
                                placeholder="Account limit"
                                value={p.keyLimits.requestsPerMinute}
                                onChange={(v): void => update(n => void (n.keyLimits.requestsPerMinute = v as number))}
                            />
                            <NumberField
                                label="Tokens / min"
                                min={1}
                                placeholder="Account limit"
                                value={p.keyLimits.tokensPerMinute}
                                onChange={(v): void => update(n => void (n.keyLimits.tokensPerMinute = v as number))}
                            />
                        </S.FormGrid>
                        {err('keyLimits') && <S.ErrorText>{err('keyLimits')}</S.ErrorText>}
                        <S.Hint>Lifetime budget per key. Account limits apply on top.</S.Hint>
                    </S.Section>
                </S.DrawerBody>

                <S.DrawerFooter>
                    {index !== -1 && !confirmClose && (
                        <S.Button variant="outlined" onClick={remove}>
                            <S.ButtonContent>
                                <Icon path={mdiDelete} size={0.75} />
                                Remove
                            </S.ButtonContent>
                        </S.Button>
                    )}
                    <S.Grow />
                    {confirmClose ? (
                        <>
                            <S.Muted>Discard changes to this profile?</S.Muted>
                            <S.Button variant="outlined" onClick={(): void => setConfirmClose(false)}>
                                Keep editing
                            </S.Button>
                            <S.Button variant="warning" onClick={onClose}>
                                Discard
                            </S.Button>
                        </>
                    ) : (
                        <>
                            <S.Button variant="outlined" onClick={requestClose}>
                                Cancel
                            </S.Button>
                            <S.Button variant="primary" disabled={!valid} onClick={apply}>
                                Apply to draft
                            </S.Button>
                        </>
                    )}
                </S.DrawerFooter>
            </S.DrawerForm>
        </S.Drawer>
    );
});
